import React, { useState, useEffect } from 'react';
import '../../main-css/chatbox-css/ChatbotVoiceModal.css'; 

const ChatbotVoicePage = ({ onMessageRecognized, BACKEND_URL, setMessages, setIsLoading, setBotLoading, isLoading, botLoading }) => {
    const [isListening, setIsListening] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isBotSpeaking, setIsBotSpeaking] = useState(false);
    const [currentEmotion, setCurrentEmotion] = useState({
        open: "/Images/Ishtar/IshtarNeutralOpen.png",
        closed: "/Images/Ishtar/IshtarNeutral.png"
    });
    const [mouthOpen, setMouthOpen] = useState(false);
    let mouthInterval = null

    const audioQueue = [];
    let isProcessingQueue = false;

    //Clean Message Emotion Tag
    const cleanMessage = (message) => {
        return message.replace(/\[emotion:.*?\]/, '').trim();
    };

    //Bot Emotion
    const getEmotionBot = (message) => {
        const emotionMatch = message.match(/\[emotion:(.*?)\]/);
        const emotion = emotionMatch ? emotionMatch[1] : 'neutral';
        const botEmotions = {
            happy: {
                open: '/Images/Ishtar/IshtarHappyOpen.png',
                closed: '/Images/Ishtar/IshtarHappy.png'
            },
            sad: {
                open: '/Images/Ishtar/IshtarSadOpen.png',
                closed: '/Images/Ishtar/IshtarSad.png'
            },
            angry: {
                open: '/Images/Ishtar/IshtarAngryOpen.png',
                closed: '/Images/Ishtar/IshtarAngry.png'
            },
            surprised: {
                open: '/Images/Ishtar/IshtarSurprisedOpen.png',
                closed: '/Images/Ishtar/IshtarSurprised.png'
            },
            neutral: {
                open: '/Images/Ishtar/IshtarNeutralOpen.png',
                closed: '/Images/Ishtar/IshtarNeutral.png'
            }
        };
        return botEmotions[emotion] || botEmotions.neutral;
    };

    const sendMessage = async (messageToSend) => {
        if (!messageToSend.trim() || isLoading) return;
        
        setMessages((prevMessages) => [
            ...prevMessages,
            { text: messageToSend, sender: 'user' },
        ]);
    
        setBotLoading(true);
        setIsLoading(true);
    
        try {
            const response = await fetch(`${BACKEND_URL}/chat`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: messageToSend }),
            });
    
            const data = await response.json();
            let responseText = data.response;

            //Extract emotion & clean message
            const emotionImages = getEmotionBot(responseText);
            setCurrentEmotion(emotionImages);
            responseText = cleanMessage(responseText);
    
            // Split response into sentences
            const sentences = responseText.split(/(?<!\w\.\w.)(?<![A-Z][a-z]\.)(?<=\.|\?|!)\s+/);
    
            let groupedSentences = [];
            for (let i = 0; i < sentences.length; i += 3) {
                const pair = sentences.slice(i, i + 3).join(' ').trim();
    
                groupedSentences.push({
                    text: pair,
                    sender: 'bot',
                    key: `history-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    isHTML: true,
                });
            }
    
            setMessages((prevMessages) => [...prevMessages, ...groupedSentences]);

            //TTS Bot
            for(const sentence of groupedSentences){
                await speak(sentence.text);
            }
            // Reset to neutral after talking
            //setTimeout(() => setBotImage(getEmotionBot('[emotion:neutral]')), 1000);

        } catch (error) {
            console.error('Error:', error);
            setMessages((prevMessages) => [
                ...prevMessages,
                { text: "Sorry, I can't think of a response.", sender: 'bot' },
            ]);
        } finally {
            setIsLoading(false);
            setBotLoading(false);
        }
    };

    // Start Listening
    const startSpeechRecognition = () => {
        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
        recognition.lang = 'en-US';
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onspeechend = () => {
            setIsListening(false);
            recognition.stop();
        };

        recognition.onerror = (event) => {
            console.error('Speech Rec Error:', event.error);
            setIsListening(false);
        };

        recognition.onresult = (event) => {
            const transcript = event.results[event.resultIndex][0].transcript;
            if(event.results[event.resultIndex].isFinal){
                onMessageRecognized(transcript); // Pass recognized transcript back
                sendMessage(transcript);
            }  
        };

        recognition.start();
    };


    //Bot TTS
    const processQueue = async () => {
        if (isProcessingQueue || audioQueue.length === 0) return;
    
        isProcessingQueue = true;
    
        // Process each TTS message in the queue
        while (audioQueue.length > 0) {
            const { text, resolve, reject } = audioQueue.shift();  // Get next message
    
            try {
                await speakInternal(text);  // Wait for the audio to finish playing
                resolve();
            } catch (error) {
                reject(error);
            }
        }
    
        isProcessingQueue = false;
    };
    
    const speakInternal = (text) => {
        return new Promise((resolve, reject) => {
            fetch(`${BACKEND_URL}/chat/api/tts`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            })
                .then(response => {
                    if (!response.ok) {
                        return response.text().then(errorText => {
                            reject(new Error(`Failed to fetch audio: ${errorText}`));
                        });
                    }
                    return response.blob();
                })
                .then(audioBlob => {
                    const audioUrl = URL.createObjectURL(audioBlob);
                    const audio = new Audio(audioUrl);
                    const audioContext = new AudioContext();
                    const source = audioContext.createMediaElementSource(audio);
                    const analyser = audioContext.createAnalyser();
    
                    source.connect(analyser);
                    analyser.connect(audioContext.destination);
                    analyser.fftSize = 256;
    
                    const bufferLength = analyser.frequencyBinCount;
                    const dataArray = new Uint8Array(bufferLength);
    
                    const updateMouth = () => {
                        analyser.getByteFrequencyData(dataArray);
                        const volume = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
                        console.log("Volume:", volume);
    
                        setMouthOpen(volume > 30); // Only update mouth state
    
                        if (!audio.paused) {
                            requestAnimationFrame(updateMouth);
                        }
                    };
    
                    audio.onplay = () => {
                        setIsBotSpeaking(true);
                        updateMouth();
                    };
    
                    audio.onended = () => {
                        setIsBotSpeaking(false);
                        setMouthOpen(false);
                        resolve();
                    };
    
                    audio.onerror = reject;
                    audio.play();
                })
                .catch(reject);
        });
    };
    
    // Modify the speak function to add audio to the queue
    const speak = (text) => {
        return new Promise((resolve, reject) => {
            audioQueue.push({ text, resolve, reject });
            processQueue();
        });
    };

    const handleOpenModal = () => {
        setModalOpen(true);
        startSpeechRecognition();
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setIsListening(false);
    };

    //Talk after Bot Finishes Replying
    useEffect(() => {
        if(!botLoading && modalOpen) startSpeechRecognition();
    },[botLoading]);

    return (
        <div>
            <button className="button" onClick={handleOpenModal} disabled={isListening}>
                <img src="/Images/mic-thumb.png" alt="Microphone"/>
            </button>

            {modalOpen && (
                <div className="bot-modal-overlay" onMouseDown={handleCloseModal}>
                    <div className="bot-modal-content" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="bot-modal-header">
                            <img
                                src={mouthOpen ? currentEmotion.open : currentEmotion.closed}
                                alt="Logo"
                                className={`logo ${isListening ? 'active' : isBotSpeaking ? 'bot-speaking' : 'done'}`}
                            />
                        </div>
                        <div className={`mic-waveform ${isListening ? 'active' : ''} ${isBotSpeaking ? 'bot':''}`}>
                            <div className="wave"></div>
                            <div className="wave"></div>
                            <div className="wave"></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatbotVoicePage;